// Tools.jsx - The calculator index. A short page whose job is to route people to
// whichever tool matches what is sitting on their table.

import { Link } from "react-router";

import { Seo, JsonLd, breadcrumbSchema, itemListSchema, SITE } from "../lib/seo.jsx";
import { useSpot } from "../lib/hooks.js";
import { money } from "../lib/format.js";
import { ArrowRight } from "../ui/icons.jsx";
import { StaggerBoxed } from "../ui/motion.jsx";
import { Loupe } from "../ui/illustrations.jsx";
import { PageHead, CtaBand, MobileCta, EmbedThis } from "./parts.jsx";

const TOOLS = [
  {
    to: "/tools/gold-calculator",
    title: "Scrap gold calculator",
    body: "10K, 14K, 18K, 22K and 24K. Add as many weights as you like and it totals the pure gold content and its value at today's price.",
    for: "Chains, rings, class rings, single earrings, dental gold",
  },
  {
    to: "/tools/silver-calculator",
    title: "Silver calculator",
    body: "Sterling, coin silver and fine silver. Built for flatware sets, which are heavier than almost everybody expects.",
    for: "Flatware, hollowware, .925 jewellery, pre-1965 US coins",
  },
  {
    to: "/tools/platinum-calculator",
    title: "Platinum calculator",
    body: "PLAT, 950 and 900. Platinum is denser than gold, so a small piece is worth checking properly.",
    for: "Ring settings, wedding bands, lab and industrial scrap",
  },
  {
    to: "/tools/karat-chart",
    title: "Karat and hallmark chart",
    body: "Every marking you are likely to find stamped on a piece, what it means, and which ones mean plated rather than solid.",
    for: "Anything with a stamp you cannot read",
  },
  {
    to: "/tools/weight-converter",
    title: "Weight converter",
    body: "Grams, pennyweight, troy ounces and pounds. A troy ounce is not the ounce on your kitchen scale, which trips up nearly everyone.",
    for: "Reading a scale, or checking somebody else's numbers",
  },
  {
    to: "/prices",
    title: "Live metal prices",
    body: "Gold, silver, platinum and palladium, per ounce, per gram and per pennyweight, updated every minute.",
    for: "Checking what the market is doing today",
  },
];

export default function Tools() {
  const { spot } = useSpot();

  return (
    <>
      <Seo
        title="Precious metal calculators"
        path="/tools"
        description="Free calculators for scrap gold, sterling silver and platinum. Work out the pure metal content of any piece and what it is worth at today's live spot price."
        image={SITE.ogTools}
      />
      <JsonLd data={breadcrumbSchema([{ label: "Home", to: "/" }, { label: "Calculators", to: "/tools" }])} />
      {/* Names what is in the list before a crawler follows a single link in it. */}
      <JsonLd data={itemListSchema({
        name: "Precious metal calculators",
        items: [
          { label: "Scrap gold calculator", to: "/tools/gold-calculator" },
          { label: "Silver calculator", to: "/tools/silver-calculator" },
          { label: "Platinum calculator", to: "/tools/platinum-calculator" },
          { label: "Karat and hallmark chart", to: "/tools/karat-chart" },
          { label: "Weight converter", to: "/tools/weight-converter" },
        ],
      })} />

      <PageHead
        eyebrow="Free calculators"
        title="Work it out yourself first."
        lede="These give you the metal content at spot, which is the number every buyer starts from. Knowing it is the difference between accepting an offer and understanding one."
        art={() => <Loupe className="max-w-[15rem] ml-auto" />}
      >
        {spot?.perOunce?.gold && (
          <p className="hint num">
            Gold {money(spot.perOunce.gold)} &middot; Silver {money(spot.perOunce.silver)} &middot; Platinum {money(spot.perOunce.platinum)} per troy ounce, live
          </p>
        )}
      </PageHead>

      <div className="shell pb-14 md:pb-24">
        <StaggerBoxed
          as="div"
          step={70}
          className="grid gap-px bg-rule border border-rule md:grid-cols-2"
          itemClassName="bg-paper h-full"
        >
          {TOOLS.map((tool) => (
            <Link key={tool.to} to={tool.to} className="flex flex-col h-full bg-paper p-6 sm:p-7 md:p-8 group hover:bg-surface transition-colors">
              <h2 className="display display-md text-ink flex items-start justify-between gap-4">
                <span className="underline-grow">{tool.title}</span>
                <ArrowRight size={20} className="text-faint shrink-0 mt-1.5 transition-transform group-hover:translate-x-1 group-hover:text-bronze" />
              </h2>
              <p className="hint mt-3 leading-relaxed flex-1">{tool.body}</p>
              <p className="mt-4 pt-4 border-t border-rule-soft text-[12.5px] text-faint">
                <span className="uppercase tracking-[0.1em] font-semibold">For</span> {tool.for}
              </p>
            </Link>
          ))}
        </StaggerBoxed>
      </div>

      <div className="shell pb-10">
        <EmbedThis tool="calculator" metal="gold" />
      </div>

      <CtaBand />
      <MobileCta />
    </>
  );
}
